import axios from 'axios';

/**
 * Client for the advertisement API endpoints
 */
class AdvertisementClient {
  constructor(backendUrl) {
    // Remove trailing slash to prevent double slashes in URLs
    this.backendUrl = backendUrl?.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
  }

  // ==================== Admin Endpoints ====================

  /**
   * Get all advertisements for a company with optional filters
   * @param {string} companyId - Company ID
   * @param {object} filters - Optional filters (status, templateType, search)
   */
  async getAdvertisements(companyId, filters = {}) {
    const params = { companyId, ...filters };
    
    return axios.get(`${this.backendUrl}/admin/advertisements`, {
      params,
      headers: {
        'X-User-Id': localStorage.getItem('userId'),
        'X-Company-Id': companyId
      }
    });
  }

  /**
   * Create a new advertisement
   * @param {object} data - Advertisement data
   */
  async createAdvertisement(data) {
    return axios.post(`${this.backendUrl}/admin/advertisements`, data, {
      headers: {
        'X-User-Id': localStorage.getItem('userId'),
        'X-Company-Id': data.companyId
      }
    });
  }

  /**
   * Get a single advertisement by ID
   * @param {string} id - Advertisement ID
   */
  async getAdvertisement(id) {
    return axios.get(`${this.backendUrl}/admin/advertisements/${id}`, {
      headers: {
        'X-User-Id': localStorage.getItem('userId')
      }
    });
  }

  /**
   * Update an advertisement
   * @param {string} id - Advertisement ID
   * @param {object} data - Updated advertisement data
   */
  async updateAdvertisement(id, data) {
    return axios.put(`${this.backendUrl}/admin/advertisements/${id}`, data, {
      headers: {
        'X-User-Id': localStorage.getItem('userId'),
        'X-Company-Id': data.companyId
      }
    });
  }

  /**
   * Delete an advertisement
   * @param {string} id - Advertisement ID
   */
  async deleteAdvertisement(id) {
    return axios.delete(`${this.backendUrl}/admin/advertisements/${id}`, {
      headers: {
        'X-User-Id': localStorage.getItem('userId')
      }
    });
  }

  /**
   * Activate an advertisement
   * @param {string} id - Advertisement ID
   */
  async activateAdvertisement(id) {
    return axios.post(`${this.backendUrl}/admin/advertisements/${id}/activate`, {}, {
      headers: {
        'X-User-Id': localStorage.getItem('userId')
      }
    });
  }

  /**
   * Deactivate an advertisement
   * @param {string} id - Advertisement ID
   */
  async deactivateAdvertisement(id) {
    return axios.post(`${this.backendUrl}/admin/advertisements/${id}/deactivate`, {}, {
      headers: {
        'X-User-Id': localStorage.getItem('userId')
      }
    });
  }

  /**
   * Duplicate an advertisement
   * @param {string} id - Advertisement ID to duplicate
   */
  async duplicateAdvertisement(id) {
    return axios.post(`${this.backendUrl}/admin/advertisements/${id}/duplicate`, {}, {
      headers: {
        'X-User-Id': localStorage.getItem('userId')
      }
    });
  }

  /**
   * Upload an advertisement image
   * @param {File} file - Image file to upload
   * @param {string} companyId - Company ID
   */
  async uploadAdvertisementImage(file, companyId) {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('companyId', companyId);
    
    // Do NOT set Content-Type header manually - axios will set it with the correct boundary
    return axios.post(`${this.backendUrl}/admin/advertisements/upload-image`, formData, {
      headers: {
        'X-User-Id': localStorage.getItem('userId'),
        'X-Company-Id': companyId
      }
    });
  }

  // ==================== User Endpoints ====================

  /**
   * Get active advertisements for the current user's company
   */
  async getActiveAdvertisements() {
    const companyId = localStorage.getItem('companyId');
    const userId = localStorage.getItem('userId');
    
    return axios.get(`${this.backendUrl}/advertisements/active`, {
      headers: {
        'X-User-Id': userId,
        'X-Company-Id': companyId
      }
    });
  }

  /**
   * Mark an advertisement as seen
   * @param {string} id - Advertisement ID
   */
  async markAdvertisementSeen(id) {
    return axios.post(`${this.backendUrl}/advertisements/${id}/seen`, {}, {
      headers: {
        'X-User-Id': localStorage.getItem('userId')
      }
    });
  }

  /**
   * Dismiss an advertisement (hide it for the current user)
   * @param {string} id - Advertisement ID
   */
  async dismissAdvertisement(id) {
    return axios.post(`${this.backendUrl}/advertisements/${id}/dismiss`, {}, {
      headers: {
        'X-User-Id': localStorage.getItem('userId')
      }
    });
  }
}

export default AdvertisementClient;
